import { create } from 'zustand';
import { notificationsApi, AppNotification } from '../api/client';

const POLL_INTERVAL_MS = 60_000;

interface NotificationState {
  unreadCount: number;
  notifications: AppNotification[];
  isLoading: boolean;
  lastFetchedAt: Date | null;

  /** Start polling for unread count. Returns cleanup function. */
  initialize: () => () => void;
  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  markAllRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  /** Reset state on logout. */
  clear: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  unreadCount: 0,
  notifications: [],
  isLoading: false,
  lastFetchedAt: null,

  initialize: () => {
    // Fetch immediately on init
    get().fetchUnreadCount();

    const intervalId = setInterval(() => {
      get().fetchUnreadCount();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  },

  fetchUnreadCount: async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      set({ unreadCount: res.data.unread_count });
    } catch {
      // Silently ignore — this is a background poll
    }
  },

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const res = await notificationsApi.list({ limit: 50 });
      set({
        notifications: res.data.items,
        unreadCount: res.data.unread_count,
        lastFetchedAt: new Date(),
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  markRead: async (ids: string[]) => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) =>
        ids.includes(n.id) ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
      ),
      unreadCount: Math.max(
        0,
        state.unreadCount - state.notifications.filter((n) => ids.includes(n.id) && !n.is_read).length,
      ),
    }));

    try {
      await notificationsApi.markRead(ids);
    } catch {
      // Revert on failure
      get().fetchNotifications();
    }
  },

  markAllRead: async () => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        is_read: true,
        read_at: n.read_at ?? new Date().toISOString(),
      })),
      unreadCount: 0,
    }));

    try {
      await notificationsApi.markRead();
    } catch {
      get().fetchNotifications();
    }
  },

  deleteNotification: async (id: string) => {
    const prev = get().notifications;
    const target = prev.find((n) => n.id === id);

    // Optimistic update
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: target && !target.is_read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));

    try {
      await notificationsApi.delete(id);
    } catch {
      set({ notifications: prev });
      get().fetchUnreadCount();
    }
  },

  clear: () => {
    set({
      unreadCount: 0,
      notifications: [],
      isLoading: false,
      lastFetchedAt: null,
    });
  },
}));
